import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  Layers, ArrowUp, ArrowDown, Eye, EyeOff, Save, Loader2, ExternalLink,
  ChevronDown, ChevronUp, Pencil, ImagePlus, Sparkles, Lock, Hash,
} from "lucide-react";
import { toast } from "sonner";
import MediaPicker from "../../components/admin/MediaPicker";

/**
 * Each section declares:
 * - key, label, icon (emoji), color (tailwind gradient)
 * - description
 * - shopOnly: displayed only if shop_enabled
 * - editableContent: keys of home_sections_content[key] that can be edited inline
 *   Each key points to a definition: { label, type: text|textarea|url|image|color|number, placeholder }
 * - externalEditor: optional { label, to } — link to the dedicated admin page (for hero, services, etc.)
 */
const SECTIONS = [
  {
    key: "promo_banner", label: "Bandeau promo", icon: "📣", color: "from-red-500 to-orange-500",
    description: "Bandeau plein largeur en tout haut du site (message + lien optionnel).",
    editableContent: {
      text: { label: "Texte", type: "text", placeholder: "Livraison offerte dès 50 €" },
      link_label: { label: "Libellé du lien", type: "text", placeholder: "Voir les offres" },
      link_url: { label: "URL du lien", type: "url", placeholder: "/shop" },
      bg: { label: "Couleur de fond", type: "color", placeholder: "#0B2242" },
      fg: { label: "Couleur du texte", type: "color", placeholder: "#FFFFFF" },
    },
  },
  {
    key: "hero", label: "Hero principal", icon: "🚀", color: "from-sky-500 to-blue-600",
    description: "Bannière d'accueil, titre, sous-titre, visuel plein écran.",
    externalEditor: { label: "Éditer le Hero dans Paramètres", to: "/admin/settings" },
  },
  {
    key: "brands", label: "Marques partenaires", icon: "🏷️", color: "from-indigo-500 to-purple-600",
    description: "Bandeau de logos des marques que vous installez.",
    externalEditor: { label: "Éditer les marques dans Paramètres", to: "/admin/settings" },
  },
  {
    key: "differentiation", label: "Pourquoi nous choisir", icon: "⭐", color: "from-emerald-500 to-teal-600",
    description: "Différenciation : vos 3-4 atouts principaux.",
    externalEditor: { label: "Éditer dans Paramètres", to: "/admin/settings" },
  },
  {
    key: "climatisation", label: "Types de climatisation", icon: "❄️", color: "from-cyan-500 to-sky-600",
    description: "Bloc métier spécifique (visible uniquement si secteur = climatisation).",
  },
  {
    key: "services", label: "Nos prestations", icon: "🛠️", color: "from-orange-500 to-amber-600",
    description: "Grille de services avec images, descriptions et liens.",
    externalEditor: { label: "Éditer les prestations", to: "/admin/services" },
  },
  {
    key: "shop_categories", label: "Catégories en carrousel", icon: "📦", color: "from-fuchsia-500 to-pink-600",
    description: "Carrousel horizontal des catégories produits.", shopOnly: true,
    editableContent: {
      kicker: { label: "Kicker (petit titre)", type: "text", placeholder: "Explorez" },
      title: { label: "Titre principal", type: "text", placeholder: "Nos catégories" },
      subtitle: { label: "Sous-titre", type: "textarea", placeholder: "Trouvez rapidement ce que vous cherchez..." },
    },
    externalEditor: { label: "Gérer les catégories produits", to: "/admin/products" },
  },
  {
    key: "shop_popular", label: "Produits populaires", icon: "🔥", color: "from-rose-500 to-red-600",
    description: "Top produits mis en avant (featured=true, fallback sur tous).", shopOnly: true,
    editableContent: {
      kicker: { label: "Kicker", type: "text", placeholder: "Best-sellers" },
      title: { label: "Titre", type: "text", placeholder: "Produits les plus populaires" },
      subtitle: { label: "Sous-titre", type: "textarea", placeholder: "Les coups de cœur de nos clients." },
      image: { label: "Icône/Image accent", type: "image", placeholder: "" },
      limit: { label: "Nombre de produits max", type: "number", placeholder: "8" },
    },
  },
  {
    key: "shop_promotions", label: "Offres du moment", icon: "🎁", color: "from-pink-500 to-rose-500",
    description: "Produits avec prix barré (compare_at > price).", shopOnly: true,
    editableContent: {
      kicker: { label: "Kicker", type: "text", placeholder: "Offres du moment" },
      title: { label: "Titre", type: "text", placeholder: "Profitez-en avant la fin !" },
      subtitle: { label: "Sous-titre", type: "textarea", placeholder: "Produits en promotion, stock limité." },
      image: { label: "Icône/Image accent", type: "image", placeholder: "" },
      limit: { label: "Nombre de produits max", type: "number", placeholder: "8" },
    },
  },
  {
    key: "shop_new", label: "Nouveautés", icon: "✨", color: "from-blue-500 to-indigo-600",
    description: "Derniers produits ajoutés au catalogue.", shopOnly: true,
    editableContent: {
      kicker: { label: "Kicker", type: "text", placeholder: "Nouveautés" },
      title: { label: "Titre", type: "text", placeholder: "Tout juste arrivés" },
      subtitle: { label: "Sous-titre", type: "textarea", placeholder: "Découvrez nos dernières nouveautés." },
      image: { label: "Icône/Image accent", type: "image", placeholder: "" },
      limit: { label: "Nombre de produits max", type: "number", placeholder: "8" },
    },
  },
  {
    key: "realisations", label: "Réalisations / Portfolio", icon: "📸", color: "from-violet-500 to-purple-600",
    description: "Galerie des dernières réalisations.",
    externalEditor: { label: "Éditer les réalisations", to: "/admin/realisations" },
  },
  {
    key: "testimonials", label: "Avis clients", icon: "💬", color: "from-yellow-500 to-orange-500",
    description: "Note Google + avis clients en carrousel.",
    externalEditor: { label: "Éditer les avis dans Paramètres", to: "/admin/settings" },
  },
  {
    key: "zones", label: "Zones d'intervention", icon: "📍", color: "from-green-500 to-emerald-600",
    description: "Villes desservies autour de votre adresse.",
    externalEditor: { label: "Éditer les zones", to: "/admin/zones" },
  },
  {
    key: "newsletter", label: "Newsletter", icon: "📧", color: "from-teal-500 to-cyan-600",
    description: "Bloc d'inscription à votre newsletter (email).",
    editableContent: {
      kicker: { label: "Kicker", type: "text", placeholder: "Restez informé" },
      title: { label: "Titre", type: "text", placeholder: "Rejoignez notre newsletter" },
      subtitle: { label: "Sous-titre", type: "textarea", placeholder: "Recevez nos offres exclusives..." },
      image: { label: "Image de fond", type: "image", placeholder: "" },
      placeholder: { label: "Placeholder du champ email", type: "text", placeholder: "votre@email.com" },
      button_label: { label: "Libellé du bouton", type: "text", placeholder: "Je m'inscris" },
    },
  },
  {
    key: "cta", label: "Bloc CTA final", icon: "📞", color: "from-red-500 to-rose-600",
    description: "Call-to-action de fin de page (devis, appel).",
    externalEditor: { label: "Éditer dans Paramètres", to: "/admin/settings" },
  },
];

const DEFAULT_ORDER = SECTIONS.map(s => s.key);

function SectionContentEditor({ section, content, onChange, onOpenPicker }) {
  const entries = Object.entries(section.editableContent || {});
  if (!entries.length) return null;

  return (
    <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`editor-${section.key}`}>
      {entries.map(([key, def]) => {
        const v = content?.[key] ?? "";
        if (def.type === "textarea") {
          return (
            <label key={key} className="md:col-span-2 flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">{def.label}</span>
              <Textarea value={v} onChange={(e) => onChange(key, e.target.value)} rows={2} placeholder={def.placeholder} className="text-sm" data-testid={`field-${section.key}-${key}`} />
            </label>
          );
        }
        if (def.type === "image") {
          return (
            <label key={key} className="md:col-span-2 flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">{def.label}</span>
              <div className="flex items-center gap-2">
                <Input value={v} onChange={(e) => onChange(key, e.target.value)} placeholder={def.placeholder || "URL de l'image"} className="text-sm flex-1" data-testid={`field-${section.key}-${key}`} />
                <Button type="button" variant="outline" size="sm" onClick={() => onOpenPicker(key)} className="text-xs shrink-0">
                  <ImagePlus className="w-3.5 h-3.5 mr-1" /> Choisir
                </Button>
                {v && <img src={v} alt="" className="w-10 h-10 rounded object-cover border border-[hsl(var(--border))]" />}
              </div>
            </label>
          );
        }
        if (def.type === "color") {
          return (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">{def.label}</span>
              <div className="flex items-center gap-2">
                <input type="color" value={v || def.placeholder || "#000000"} onChange={(e) => onChange(key, e.target.value)} className="w-10 h-9 rounded border border-[hsl(var(--border))] cursor-pointer" data-testid={`field-${section.key}-${key}`} />
                <Input value={v} onChange={(e) => onChange(key, e.target.value)} placeholder={def.placeholder} className="text-xs font-mono flex-1" />
              </div>
            </label>
          );
        }
        if (def.type === "number") {
          return (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">{def.label}</span>
              <Input type="number" min="1" max="50" value={v} onChange={(e) => onChange(key, Number(e.target.value) || 0)} placeholder={def.placeholder} className="text-sm" data-testid={`field-${section.key}-${key}`} />
            </label>
          );
        }
        return (
          <label key={key} className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">{def.label}</span>
            <Input value={v} onChange={(e) => onChange(key, e.target.value)} placeholder={def.placeholder} className="text-sm" data-testid={`field-${section.key}-${key}`} />
          </label>
        );
      })}
    </div>
  );
}

export default function HomeSectionsToggleAdmin() {
  const { settings, refresh } = useSettings();
  const [enabled, setEnabled] = useState({});
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [content, setContent] = useState({});
  const [openKey, setOpenKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [picker, setPicker] = useState(null);       // { sectionKey, fieldKey }

  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.home_sections_enabled || {});
    // Merge settings order with any new defaults so new sections appear
    const saved = Array.isArray(settings.home_sections_order) ? settings.home_sections_order : [];
    const merged = [...saved];
    for (const k of DEFAULT_ORDER) if (!merged.includes(k)) merged.push(k);
    setOrder(merged);
    setContent(settings.home_sections_content || {});
    setDirty(false);
  }, [settings]);

  const shopEnabled = !!settings?.shop_enabled;

  // Sections actually visible in the admin (hide shopOnly if shop disabled)
  const visibleOrder = useMemo(
    () => order.filter(k => {
      const s = SECTIONS.find(x => x.key === k);
      if (!s) return false;
      if (s.shopOnly && !shopEnabled) return false;
      return true;
    }),
    [order, shopEnabled]
  );

  const toggle = (key) => {
    setEnabled(e => ({ ...e, [key]: e[key] === false ? true : !(e[key] ?? true) }));
    setDirty(true);
  };

  const move = (visibleIndex, dir) => {
    const key = visibleOrder[visibleIndex];
    const targetKey = visibleOrder[visibleIndex + dir];
    if (!key || !targetKey) return;
    const nextOrder = [...order];
    const i = nextOrder.indexOf(key);
    const j = nextOrder.indexOf(targetKey);
    [nextOrder[i], nextOrder[j]] = [nextOrder[j], nextOrder[i]];
    setOrder(nextOrder);
    setDirty(true);
  };

  const updateField = (sectionKey, fieldKey, value) => {
    setContent(c => ({ ...c, [sectionKey]: { ...(c[sectionKey] || {}), [fieldKey]: value } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        home_sections_enabled: enabled,
        home_sections_order: order,
        home_sections_content: content,
      });
      await refresh();
      setDirty(false);
      toast.success("Sections enregistrées — actualisez la page d'accueil pour voir les changements");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const enabledCount = visibleOrder.filter(k => enabled[k] !== false).length;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="sections-toggle-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Layers className="w-3.5 h-3.5" /> Page d'accueil uniquement
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Sections page d'accueil</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Activez, masquez, réordonnez et personnalisez les blocs affichés <strong>uniquement sur la page d'accueil</strong>.
            Chaque section possède un <strong className="text-[color:var(--brand-navy)]">numéro Sx</strong> à communiquer à votre agence pour tout redesign.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs font-bold px-3 py-1.5">
            {enabledCount} / {visibleOrder.length} visibles
          </Badge>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold h-9 px-3 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
            data-testid="preview-home-link"
          >
            Voir la page d'accueil <ExternalLink className="w-3 h-3" />
          </a>
          <Button onClick={save} disabled={!dirty || saving} className="bg-[color:var(--brand-navy)]" data-testid="save-sections-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {dirty ? "Enregistrer" : "Enregistré"}
          </Button>
        </div>
      </header>

      <Card className="p-4 bg-gradient-to-br from-[color:var(--brand-navy)] via-[color:var(--brand-navy)] to-[color:var(--brand-sky)] text-white border-0">
        <div className="flex items-center gap-3 text-xs">
          <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Hash className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-sm mb-0.5">Chaque section a un numéro (S1, S2, …)</div>
            <div className="text-white/85 text-[11px] leading-relaxed">
              Ces références permettent à votre agence — via le <strong className="text-white">Studio IA</strong> accessible après appairage à l'Accès Agence — de cibler précisément la section à modifier
              (ex : <em>"Agrandis l'image du hero S2, aligne le texte à droite"</em>), tout en préservant les données dynamiques gérées ici.
            </div>
          </div>
        </div>
      </Card>

      {!shopEnabled && (
        <Card className="p-3 bg-amber-50 border-amber-200 text-xs text-amber-900 flex items-start gap-2">
          <Lock className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Boutique désactivée :</strong> les sections <em>Catégories, Produits populaires, Offres du moment, Nouveautés</em> sont masquées. Activez la boutique dans&nbsp;
            <a href="/admin/widgets" className="underline font-semibold">Console widgets</a>.
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {visibleOrder.map((key, index) => {
          const section = SECTIONS.find(s => s.key === key);
          if (!section) return null;
          const sectionNumber = `S${index + 1}`;
          const isOn = enabled[key] !== false;
          const isOpen = openKey === key;
          const hasInlineEditor = !!section.editableContent;
          return (
            <Card
              key={key}
              className={`transition-all overflow-hidden ${isOn ? "" : "opacity-60"}`}
              data-testid={`section-row-${key}`}
            >
              <div className="p-4 flex items-center gap-3 md:gap-4 flex-wrap">
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="w-6 h-6 rounded-md hover:bg-[hsl(var(--muted))] flex items-center justify-center disabled:opacity-20 disabled:cursor-not-allowed"
                    data-testid={`move-up-${key}`}
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <span className="text-[10px] text-center font-mono text-[hsl(var(--muted-foreground))]">{index + 1}</span>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === visibleOrder.length - 1}
                    className="w-6 h-6 rounded-md hover:bg-[hsl(var(--muted))] flex items-center justify-center disabled:opacity-20 disabled:cursor-not-allowed"
                    data-testid={`move-down-${key}`}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>

                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center text-white text-xl shrink-0 ${!isOn ? "grayscale" : ""}`}>
                  {section.icon}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] font-black px-1.5 py-0.5 rounded bg-[color:var(--brand-navy)] text-white tracking-wider" data-testid={`section-number-${key}`}>
                      {sectionNumber}
                    </span>
                    <span className="font-bold text-[color:var(--brand-navy)]">{section.label}</span>
                    <Badge className={`text-[9px] uppercase tracking-wider border-0 ${isOn ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                      {isOn ? <><Eye className="w-2.5 h-2.5 mr-0.5 inline" /> Visible</> : <><EyeOff className="w-2.5 h-2.5 mr-0.5 inline" /> Masqué</>}
                    </Badge>
                    {section.shopOnly && <Badge className="text-[9px] uppercase tracking-wider border-0 bg-orange-100 text-orange-700">Boutique</Badge>}
                  </div>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{section.description}</p>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {hasInlineEditor && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenKey(isOpen ? null : key)}
                      className="text-xs h-8"
                      data-testid={`edit-content-${key}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Contenu
                      {isOpen ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                    </Button>
                  )}
                  {section.externalEditor && (
                    <a
                      href={section.externalEditor.to}
                      className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-[hsl(var(--border))] text-xs hover:bg-[hsl(var(--muted))]"
                      title={section.externalEditor.label}
                      data-testid={`open-editor-${key}`}
                    >
                      <Pencil className="w-3 h-3" /> <span className="hidden md:inline">Éditeur</span>
                    </a>
                  )}
                  <Switch
                    checked={isOn}
                    onCheckedChange={() => toggle(key)}
                    data-testid={`section-toggle-${key}`}
                  />
                </div>
              </div>

              {isOpen && hasInlineEditor && (
                <div className="px-4 pb-4 bg-[hsl(var(--muted))]/30">
                  <SectionContentEditor
                    section={section}
                    content={content[key] || {}}
                    onChange={(fk, v) => updateField(key, fk, v)}
                    onOpenPicker={(fk) => setPicker({ sectionKey: key, fieldKey: fk })}
                  />
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {dirty && (
        <div className="sticky bottom-4 flex justify-center z-30">
          <Card className="p-3 bg-[color:var(--brand-navy)] text-white border-0 shadow-2xl flex items-center gap-3">
            <span className="text-xs">Modifications non enregistrées</span>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90" data-testid="save-sections-floating">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Enregistrer
            </Button>
          </Card>
        </div>
      )}

      {picker && (
        <MediaPicker
          folder="home-sections"
          onClose={() => setPicker(null)}
          onPick={(url) => {
            updateField(picker.sectionKey, picker.fieldKey, url);
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}
